import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";
import { EmptyState } from "../recharts/ReMixCharts/components/EmptyState";
import { CriticalBandPill } from "../recharts/ReMixCharts/components/CriticalBandPill";
import { CriticalPeriodsPanel } from "../recharts/ReMixCharts/components/CriticalPeriodsPanel";
import type {
  CriticalPeriodDef,
  CriticalPeriodKind,
} from "../../models/chart_model";
import "../recharts/ReMixCharts/ReMixChart.css";
import { useEmptyStateVisibility } from "../recharts/ReMixCharts/hooks/useEmptyStateVisibility";
import { LegendSummaryTableProps, SortConfig } from "./types";
import { formatCellValue } from "./formatCellValue";

export const ChartTable: React.FC<LegendSummaryTableProps> = ({
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
  block_key,
  filename,
  itemsPerPage: itemsPerPageProp,
  defaultSortKey: defaultSortKeyProp,
  defaultSortDirection,
  isSmall = false,
  serverSidePagination = false,
  totalCount = 0,
  currentServerPage = 1,
  onServerPageChange,
  onServerItemsPerPageChange,
  serverLoading = false,
  serverTotalPages,
  tableHeight,
  isLoading,
  emptyStateDelayMs = 450,
  editableColumn,
  showCriticalPeriods = false,
  cpActive: controlledCpActive,
  onCpActiveChange,
  hideCriticalPeriodsButton = false,
  disableCopy = false,
}) => {
  const blockCopyEvent = (e: React.SyntheticEvent) => {
    if (disableCopy) e.preventDefault();
  };
  // Critical period toggle. Controlled when the parent passes cpActive (state
  // persists across Chart↔Table switches, resets on refresh); otherwise internal
  // + sessionStorage (legacy, mirrors a paired uncontrolled ReMixChart).
  const CP_STORAGE_KEY = "remix_cp_active_v2";
  const isControlled = controlledCpActive !== undefined;
  const [internalCpActive, setInternalCpActive] = useState<boolean>(() => {
    if (isControlled || !showCriticalPeriods) return false;
    try {
      return sessionStorage.getItem(CP_STORAGE_KEY) === "on";
    } catch {
      return false;
    }
  });
  const cpActive = controlledCpActive ?? internalCpActive;
  const toggleCp = () => {
    const next = !cpActive;
    if (onCpActiveChange) {
      onCpActiveChange(next);
    } else {
      try {
        sessionStorage.setItem(CP_STORAGE_KEY, next ? "on" : "off");
      } catch {}
      setInternalCpActive(next);
    }
  };

  // Static period defs matching the row-highlight hours below; the table has no
  // per-kind toggles, so the shared panel sees all-on or all-off.
  const cpPeriods = useMemo<CriticalPeriodDef[]>(
    () => [
      { kind: "solar", label: "Solar Hours", startHour: 10, endHour: 16 },
      { kind: "evening_ramp", label: "Evening Peak", startHour: 17, endHour: 21 },
      { kind: "valley", label: "Night Low", startHour: 0, endHour: 5 },
    ],
    [],
  );
  const cpActiveKinds = useMemo<Set<CriticalPeriodKind>>(
    () => (cpActive ? new Set(cpPeriods.map((p) => p.kind)) : new Set()),
    [cpActive, cpPeriods],
  );

  // Map block number or time string → critical period kind for row highlighting.
  // Solar Hours 10-16h, Evening Peak 17-21h, Night Low 0-5h.
  const getCriticalPeriodKind = useMemo(() => {
    return (blockVal: any): "solar" | "evening" | "night" | null => {
      if (!showCriticalPeriods || !cpActive || blockVal == null) return null;
      let hour: number | null = null;
      const n = Number(blockVal);
      if (Number.isFinite(n) && n >= 1) {
        hour = n > 48 ? Math.floor((n - 1) / 4) : Math.floor((n - 1) / 2);
      } else {
        const s = String(blockVal);
        const m =
          s.match(/^(\d{1,2}):/) ||
          s.match(/T(\d{2}):/) ||
          s.match(/ (\d{2}):/);
        if (m) hour = parseInt(m[1], 10);
      }
      if (hour === null) return null;
      if (hour >= 10 && hour < 16) return "solar";
      if (hour >= 17 && hour < 21) return "evening";
      if (hour >= 0 && hour < 5) return "night";
      return null;
    };
  }, [showCriticalPeriods, cpActive]);

  const dispatch = useDispatch();
  const defaultOrder = useMemo(
    () => [...extraKeys, ...legends.map((l) => l.dataKey)],
    [extraKeys, legends],
  );
  const defaultOrderStr = useMemo(() => defaultOrder.join(","), [defaultOrder]);

  const layoutStorageKeyBase = useMemo(() => {
    if (!filename) return "";
    return `ct_${filename}__${defaultOrder.join("__")}`;
  }, [filename, defaultOrder]);
  const orderStorageKey =
    layoutStorageKeyBase.length > 0 ? `${layoutStorageKeyBase}_order` : "";
  const widthStorageKey =
    layoutStorageKeyBase.length > 0 ? `${layoutStorageKeyBase}_widths` : "";

  const defaultSortKey =
    defaultSortKeyProp ||
    (extraKeys.length > 0 ? extraKeys[extraKeys.length - 1] : "");
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    composite: false,
    key: defaultSortKey,
    direction: defaultSortDirection ?? "desc",
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageProp || 96,
  );
  const [jumpValue, setJumpValue] = useState<string>("");
  const [rowsDropdownOpen, setRowsDropdownOpen] = useState(false);
  const rowsDropdownRef = useRef<HTMLDivElement | null>(null);

  // ── Column drag-to-reorder + resize state ──
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);
  const [recentlyMovedKey, setRecentlyMovedKey] = useState<string | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  // ── Inline edit state (single cell at a time) ──
  const [editingCellKey, setEditingCellKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>("");
  const [draftError, setDraftError] = useState<string>("");

  const startCellEdit = (key: string, current: any) => {
    setEditingCellKey(key);
    setDraftValue(
      current === null || current === undefined || current === "-"
        ? ""
        : String(current),
    );
    setDraftError("");
  };

  const cancelCellEdit = () => {
    setEditingCellKey(null);
    setDraftValue("");
    setDraftError("");
  };

  const editableColumns = React.useMemo(
    () =>
      editableColumn == null
        ? []
        : Array.isArray(editableColumn)
          ? editableColumn
          : [editableColumn],
    [editableColumn],
  );
  const editableByKey = React.useMemo(() => {
    const m = new Map<string, (typeof editableColumns)[number]>();
    for (const c of editableColumns) m.set(c.dataKey, c);
    return m;
  }, [editableColumns]);

  const confirmCellEdit = (
    row: any,
    activeCol: (typeof editableColumns)[number] | undefined,
    options: { cancelOnInvalid?: boolean } = {},
  ) => {
    if (!activeCol) return cancelCellEdit();
    const trimmed = draftValue.trim();
    if (trimmed === "") return cancelCellEdit();
    const n = Number(trimmed);
    if (isNaN(n)) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError("Must be a number");
      return;
    }
    if (activeCol.min !== undefined && n < activeCol.min) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError(`Min ${activeCol.min}`);
      return;
    }
    if (activeCol.max !== undefined && n > activeCol.max) {
      if (options.cancelOnInvalid) return cancelCellEdit();
      setDraftError(`Max ${activeCol.max}`);
      return;
    }
    const prev = Number(row[activeCol.dataKey]);
    if (Number.isFinite(prev) && Math.abs(prev - n) < 1e-9) {
      return cancelCellEdit();
    }
    activeCol.onCellChange({
      rowKeyValue: row[activeCol.rowKey],
      newValue: n,
      row,
    });
    cancelCellEdit();
  };

  // Refs to measure each header's natural content width
  const headerContentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [headerMinWidths, setHeaderMinWidths] = useState<
    Record<string, number>
  >({});

  // Measured actual th offsetWidths for accurate sticky left positions
  const theadRowRef = useRef<HTMLTableRowElement | null>(null);
  const [measuredThWidths, setMeasuredThWidths] = useState<
    Record<string, number>
  >({});

  // Track what has been saved so we can show "unsaved changes" on the Save button
  const [savedSignature, setSavedSignature] = useState<string>("");

  const currentSignature = useMemo(
    () => JSON.stringify(columnOrder) + "|" + JSON.stringify(columnWidths),
    [columnOrder, columnWidths],
  );

  const hasChanges =
    Object.keys(columnWidths).length > 0 ||
    columnOrder.join(",") !== defaultOrderStr;

  // isDirty: only true when user HAS made changes AND those changes aren't saved yet
  // (prevents "Save layout" showing on fresh load with nothing saved)
  const isDirty =
    !!filename && hasChanges && currentSignature !== savedSignature;

  // Load persisted layout whenever table layout scope changes (filename/columns).
  useEffect(() => {
    setColumnOrder(defaultOrder);
    setColumnWidths({});

    if (!orderStorageKey || !widthStorageKey) {
      setSavedSignature("");
      return;
    }

    try {
      const savedOrderRaw = localStorage.getItem(orderStorageKey);
      const savedWidthsRaw = localStorage.getItem(widthStorageKey);

      const nextOrder = savedOrderRaw
        ? (() => {
            const parsed = JSON.parse(savedOrderRaw) as string[];
            const kept = parsed.filter((k) => defaultOrder.includes(k));
            const added = defaultOrder.filter((k) => !parsed.includes(k));
            return [...kept, ...added];
          })()
        : defaultOrder;

      const nextWidths = savedWidthsRaw
        ? (JSON.parse(savedWidthsRaw) as Record<string, number>)
        : {};

      setColumnOrder(nextOrder);
      setColumnWidths(nextWidths);
      setSavedSignature(
        JSON.stringify(nextOrder) + "|" + JSON.stringify(nextWidths),
      );
    } catch {
      setSavedSignature("");
    }
  }, [defaultOrder, orderStorageKey, widthStorageKey]);

  // Reset to page 1 whenever the incoming data changes (e.g. filter applied)
  useEffect(() => {
    if (!serverSidePagination) {
      setCurrentPage(1);
    }
  }, [data, serverSidePagination]);

  const effectivePage = serverSidePagination ? currentServerPage : currentPage;

  useEffect(() => {
    setSelectedRowIndex(null);
  }, [effectivePage, data]);

  useEffect(() => {
    setHoveredRowIndex(null);
  }, [effectivePage, data]);

  const handlePageSet = (page: number) => {
    if (serverSidePagination && onServerPageChange) {
      onServerPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  const handleSort = (columnKey: string) => {
    if (sortConfig.composite || sortConfig.key !== columnKey) {
      setSortConfig({ composite: false, key: columnKey, direction: "desc" });
    } else {
      setSortConfig({
        composite: false,
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    }
    // Only reset page for client-side pagination; server-side sorts current page in place
    if (!serverSidePagination) {
      handlePageSet(1);
    }
  };

  const MONTH_ABBR = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parseMonthStr = (v: string): number | null => {
    // "Apr-26" (with 2-digit year) or "Apr 2026" (with 4-digit year)
    const withYear = /^([A-Za-z]{3})[-\s](\d{2}|\d{4})$/.exec(v);
    if (withYear) {
      const mon = MONTH_ABBR.findIndex(
        (x) => x.toLowerCase() === withYear[1].toLowerCase(),
      );
      if (mon === -1) return null;
      return parseInt(withYear[2]) * 12 + mon;
    }
    // Bare month abbreviation e.g. "Apr" (monthGroup with no year)
    const bare = /^([A-Za-z]{3})$/.exec(v);
    if (bare) {
      const mon = MONTH_ABBR.findIndex(
        (x) => x.toLowerCase() === bare[1].toLowerCase(),
      );
      return mon === -1 ? null : mon;
    }
    return null;
  };

  const parseDDMMYYYY = (v: string): number | null => {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (!m) return null;
    return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime();
  };

  // "Date Index" labels like "1 Day", "10 Day" — or trend labels "Day 1",
  // "Day 10" — must sort numerically, not lexicographically (otherwise
  // "Day 10" sorts before "Day 2", and "Day 23/24" land before "Day 8/9").
  const parseDayIndex = (v: string): number | null => {
    const m = /^(?:(\d+)\s*Day|Day\s*(\d+))$/i.exec(v.trim());
    if (!m) return null;
    return parseInt(m[1] ?? m[2], 10);
  };

  // Trend week labels like "Week 1 (Jun)", "Week 12 (Dec)" must sort by week
  // number, not lexicographically (otherwise "Week 10" sorts before "Week 2").
  const parseWeekIndex = (v: string): number | null => {
    const m = /^Week\s*(\d+)/i.exec(v.trim());
    if (!m) return null;
    return parseInt(m[1], 10);
  };

  // Trend month labels like "Jun 2026" must sort chronologically (year then
  // month), not lexicographically (otherwise "Apr" sorts before "Jan").
  const parseMonthYear = (v: string): number | null => {
    const m = /^([A-Za-z]{3})\s+(\d{4})$/.exec(v.trim());
    if (!m) return null;
    const mon = MONTH_ABBR.findIndex(
      (x) => x.toLowerCase() === m[1].toLowerCase(),
    );
    if (mon === -1) return null;
    return parseInt(m[2], 10) * 12 + mon;
  };

  const compareValues = (aValue: any, bValue: any): number => {
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return -1;
    if (bValue == null) return 1;
    if (typeof aValue === "number" && typeof bValue === "number") {
      return aValue - bValue;
    }
    const aStr = aValue.toString();
    const bStr = bValue.toString();
    const aMonth = parseMonthStr(aStr);
    const bMonth = parseMonthStr(bStr);
    if (aMonth !== null && bMonth !== null) {
      return aMonth - bMonth;
    }
    const aDate = parseDDMMYYYY(aStr);
    const bDate = parseDDMMYYYY(bStr);
    if (aDate !== null && bDate !== null) {
      return aDate - bDate;
    }
    const aDay = parseDayIndex(aStr);
    const bDay = parseDayIndex(bStr);
    if (aDay !== null && bDay !== null) {
      return aDay - bDay;
    }
    const aWeek = parseWeekIndex(aStr);
    const bWeek = parseWeekIndex(bStr);
    if (aWeek !== null && bWeek !== null) {
      return aWeek - bWeek;
    }
    const aMonthYear = parseMonthYear(aStr);
    const bMonthYear = parseMonthYear(bStr);
    if (aMonthYear !== null && bMonthYear !== null) {
      return aMonthYear - bMonthYear;
    }
    return aStr.localeCompare(bStr);
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortConfig.composite) {
        let cmp = compareValues(a[extraKeys[0]], b[extraKeys[0]]);
        if (cmp === 0 && extraKeys.length > 1) {
          cmp = compareValues(a[extraKeys[1]], b[extraKeys[1]]);
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      } else if (sortConfig.key) {
        let cmp = compareValues(a[sortConfig.key], b[sortConfig.key]);
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig, extraKeys]);

  const totalPages = serverSidePagination
    ? (serverTotalPages ?? Math.ceil(totalCount / itemsPerPage))
    : Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    if (serverSidePagination) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [serverSidePagination, sortedData, currentPage, itemsPerPage]);

  const resolvedLoading = isLoading ?? serverLoading;
  const showEmpty = useEmptyStateVisibility({
    isEmpty: paginatedData.length === 0,
    isLoading: resolvedLoading,
    delayMs: emptyStateDelayMs,
  });

  const renderSortArrow = (columnKey: string) => {
    if (!sortConfig.composite && sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  function calculateLegends(legend: any) {
    const displayName = normalizeDisplayLabel(legend.name).trim();
    if (/\(forecast\)\s*$/i.test(displayName)) return displayName;

    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const trailingParenMatch = displayName.match(/\(([^)]*)\)\s*$/);
    const trailingParenValue = trailingParenMatch?.[1]?.trim().toLowerCase();
    const suffixValue = String(suffix || "")
      .trim()
      .toLowerCase();
    const cleanName =
      trailingParenValue && suffixValue && trailingParenValue === suffixValue
        ? displayName.replace(/\s*\([^)]*\)\s*$/, "").trim()
        : displayName;
    return `${cleanName} (${suffix})`;
  }

  const headerColumns = [
    ...extraKeys.map((key, index) => ({
      key,
      label: normalizeDisplayLabel(extraLabels[index] || key),
    })),
    ...legends.map((legend) => ({
      key: legend.dataKey,
      // label: `${legend.name} (${unit?.toUpperCase() ?? legend.unit})`,
      label: calculateLegends(legend),
    })),
  ];

  // Sync columnOrder when legends/extraKeys props change
  const colKeyStr = headerColumns.map((c) => c.key).join(",");
  useEffect(() => {
    setColumnOrder((prev) => {
      const keys = headerColumns.map((c) => c.key);
      const kept = prev.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !prev.includes(k));
      if (kept.length === prev.length && added.length === 0) return prev;
      return [...kept, ...added];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colKeyStr]);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((k) => headerColumns.find((c) => c.key === k))
        .filter(Boolean) as typeof headerColumns,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnOrder, colKeyStr],
  );

  const stickyColumnPositions = useMemo(() => {
    const stickyNames = new Set([
      "month",
      "year",
      "timeslot",
      "timeblock",
      "date",
      "dateindex",
      "slot",
      "block",
    ]);
    const normalize = (value: string) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const stickyCols = orderedColumns.filter((col) => {
      const k = normalize(col.key);
      const l = normalize(col.label);
      return stickyNames.has(k) || stickyNames.has(l);
    });

    const positionMap: Record<string, { left: number; showShadow: boolean }> =
      {};
    let leftOffset = 0;
    stickyCols.forEach((col, idx) => {
      const width =
        measuredThWidths[col.key] ||
        Math.max(
          columnWidths[col.key] || 0,
          headerMinWidths[col.key] || 0,
          100,
        );
      positionMap[col.key] = {
        left: leftOffset,
        showShadow: idx === stickyCols.length - 1,
      };
      leftOffset += width;
    });
    return positionMap;
  }, [orderedColumns, columnWidths, headerMinWidths, measuredThWidths]);

  const resetColumns = () => {
    setColumnOrder(headerColumns.map((c) => c.key));
    setColumnWidths({});
    if (orderStorageKey && widthStorageKey) {
      try {
        localStorage.removeItem(orderStorageKey);
        localStorage.removeItem(widthStorageKey);
      } catch {}
      setSavedSignature("");
    }
  };

  const saveColumns = () => {
    if (!orderStorageKey || !widthStorageKey) return;
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(columnOrder));
      localStorage.setItem(widthStorageKey, JSON.stringify(columnWidths));
      setSavedSignature(currentSignature);
    } catch {}
  };

  const handleDownloadCSV = () => {
    const legendsByKey = new Map(
      legends.map((legend) => [legend.dataKey, legend]),
    );
    const headers = orderedColumns.map((col) => {
      const legend = legendsByKey.get(col.key);
      if (legend) return calculateLegends(legend);
      const extraIdx = extraKeys.indexOf(col.key);
      return extraIdx !== -1
        ? normalizeDisplayLabel(extraLabels[extraIdx] || col.key)
        : col.label;
    });

    const formatNum = (value: any) => {
      if (value === undefined || value === null || value === "") return "";
      const num = Number(value);
      return isNaN(num) ? String(value) : num.toFixed(2);
    };

    const escapeCsv = (value: string | number) => {
      const str = String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = sortedData.map((row) =>
      orderedColumns.map((col) => {
        const legend = legendsByKey.get(col.key);
        if (legend) {
          const raw = row[legend.dataKey];
          if (raw == null) return "";
          const formatted = formatNum(raw);
          if (
            legend.deviationValueKey &&
            row[legend.deviationValueKey] != null
          ) {
            return escapeCsv(
              `${formatted} (${formatNum(row[legend.deviationValueKey])})`,
            );
          }
          return escapeCsv(formatted);
        }

        return escapeCsv(row[col.key] ?? "");
      }),
    );

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", (filename || "table") + " Chart Data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Measure each header label width via canvas so it's independent of layout
  // (scrollWidth returns constrained width when flex children shrink in a fixed-width th)
  useLayoutEffect(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Try to read actual font from a rendered header; fall back to approximation
    let fontStr = "";
    const sampleKey = orderedColumns[0]?.key;
    const sampleEl = sampleKey ? headerContentRefs.current[sampleKey] : null;
    if (sampleEl) {
      const cs = window.getComputedStyle(sampleEl);
      fontStr = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    } else {
      fontStr = isSmall ? "500 10px sans-serif" : "500 14px sans-serif";
    }
    ctx.font = fontStr;
    // Fixed chrome: grip icon + gaps + sort arrow + th padding + resize handle + safety
    const chrome = isSmall ? 64 : 92;
    const measured: Record<string, number> = {};
    orderedColumns.forEach((col) => {
      const w = ctx.measureText(col.label).width;
      measured[col.key] = Math.ceil(w + chrome);
    });
    setHeaderMinWidths((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(measured)) {
        if ((prev[k] || 0) !== v) {
          next[k] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orderedColumns, isSmall]);

  // Sync actual th offsetWidths so sticky left offsets are pixel-accurate
  useLayoutEffect(() => {
    const tr = theadRowRef.current;
    if (!tr) return;
    const ths = Array.from(tr.querySelectorAll("th")) as HTMLTableCellElement[];
    const next: Record<string, number> = {};
    ths.forEach((th, i) => {
      const key = orderedColumns[i]?.key;
      if (key) next[key] = th.offsetWidth;
    });
    setMeasuredThWidths((prev) => {
      const changed = orderedColumns.some(
        (c) => (prev[c.key] || 0) !== (next[c.key] || 0),
      );
      return changed ? next : prev;
    });
  }, [orderedColumns, columnWidths, headerMinWidths]);

  const renderCell = (
    col: { key: string; label: string },
    row: any,
    opts: {
      isSticky?: boolean;
      stickyLeft?: number;
      showStickyShadow?: boolean;
      isSelected?: boolean;
      isHovered?: boolean;
      cpOverlay?: React.ReactNode;
      rowIndex?: number;
      isMidRow?: boolean;
      cpKind?: "solar" | "evening" | "night" | null;
    } = {},
  ) => {
    // Table is always table-layout:fixed, so the colgroup governs column width.
    // Cells just need to clip + ellipsis their overflow; maxWidth:1px lets the
    // fixed column width win. This makes cell width independent of content, so
    // sorting (which changes visible rows) never shifts column widths.
    const fixedCellStyle: React.CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "1px",
    };
    const stickyStyle: React.CSSProperties = opts.isSticky
      ? {
          position: "sticky",
          left: opts.stickyLeft ?? 0,
          zIndex: opts.isMidRow ? 50 : 5,
          overflow: "visible",
          boxShadow: opts.showStickyShadow
            ? "8px 0 14px -12px rgba(15, 23, 42, 0.42), 14px 0 24px -20px rgba(15, 23, 42, 0.24)"
            : undefined,
          backgroundColor:
            !opts.isSelected && !opts.isHovered && opts.cpKind
              ? opts.cpKind === "solar"
                ? "rgb(255, 252, 244)"
                : opts.cpKind === "evening"
                  ? "rgb(255, 248, 243)"
                  : "rgb(245, 249, 255)"
              : undefined,
        }
      : {};
    const firstColClass = opts.isSticky
      ? opts.isSelected
        ? "bg-orange-50"
        : opts.isHovered
          ? "bg-gray-100"
          : !opts.cpKind
            ? "bg-white"
            : ""
      : "";
    const mergedCellStyle: React.CSSProperties = {
      ...fixedCellStyle,
      ...stickyStyle,
    };
    const extraIndex = extraKeys.indexOf(col.key);
    if (extraIndex !== -1) {
      const key = extraKeys[extraIndex];
      const isBlockCol = key === (block_key ?? "block");
      return (
        <td
          key={col.key}
          className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} text-center ${
            key === "block" ? "text-[#F1935C] font-medium" : ""
          } ${firstColClass}`}
          style={
            isBlockCol && opts.cpOverlay
              ? {
                  ...(mergedCellStyle || {}),
                  position: mergedCellStyle?.position || "relative",
                  zIndex: mergedCellStyle?.zIndex ?? (opts.isMidRow ? 50 : 5),
                  overflow: "visible",
                }
              : mergedCellStyle
          }
          title={row[key] !== undefined ? String(row[key]) : undefined}
        >
          {isBlockCol && opts.cpOverlay}
          {row[key] !== undefined
            ? typeof row[key] === "string"
              ? normalizeDisplayLabel(row[key])
              : row[key]
            : "-"}
        </td>
      );
    }
    const legend = legends.find((l) => l.dataKey === col.key);
    if (legend) {
      const activeEditableCol = editableByKey.get(col.key);
      const isEditableCol = !!activeEditableCol;
      const rowKeyVal = isEditableCol
        ? row[activeEditableCol!.rowKey]
        : undefined;
      const cellKey = isEditableCol
        ? `${activeEditableCol!.dataKey}__${String(rowKeyVal)}`
        : "";
      const isEditing = isEditableCol && editingCellKey === cellKey;
      const currentNumeric = isEditableCol ? Number(row[col.key]) : NaN;
      const originalNumeric =
        isEditableCol && activeEditableCol!.originalByRowKey
          ? activeEditableCol!.originalByRowKey[rowKeyVal]
          : undefined;
      const differs =
        isEditableCol &&
        typeof originalNumeric === "number" &&
        Number.isFinite(currentNumeric) &&
        Math.abs(originalNumeric - currentNumeric) > 1e-9;
      const cellText =
        legend.deviationValueKey && row[legend.deviationValueKey] != null
          ? `${formatCellValue(row[legend.dataKey])} (${formatCellValue(row[legend.deviationValueKey])})`
          : formatCellValue(row[legend.dataKey]);
      return (
        <td
          key={col.key}
          className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} ${firstColClass} ${
            isEditableCol ? "relative cursor-pointer" : ""
          }`}
          style={
            isEditing
              ? { ...(mergedCellStyle || {}), overflow: "visible" }
              : mergedCellStyle
          }
          title={!isEditing ? cellText : undefined}
          onClick={(e) => {
            if (!isEditableCol || isEditing) return;
            e.stopPropagation();
            startCellEdit(cellKey, row[activeEditableCol!.dataKey]);
          }}
        >
          {differs && !isEditing && activeEditableCol!.onRevert && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                activeEditableCol!.onRevert!(rowKeyVal);
              }}
              className="absolute top-0 right-0 bg-amber-500 hover:bg-amber-600 text-white rounded-bl text-[10px] px-1.5 py-[1px] leading-tight shadow z-[9]"
              title="Revert"
            >
              ↺
            </button>
          )}
          {!isEditing && (
            <span className={differs ? "text-[#F1935C] font-semibold" : ""}>
              {cellText}
            </span>
          )}
          {isEditing && (
            <div className="absolute z-[9999] inset-0 flex items-center justify-center">
              <div className="flex flex-col items-stretch">
                {draftError && (
                  <div className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 mb-0.5 whitespace-nowrap">
                    {draftError}
                  </div>
                )}
                <div className="flex border border-gray-300 shadow-lg bg-white rounded">
                  <input
                    autoFocus
                    type="number"
                    inputMode="decimal"
                    step={activeEditableCol!.step ?? "any"}
                    min={activeEditableCol!.min}
                    max={activeEditableCol!.max}
                    className="w-20 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0 rounded-l"
                    value={draftValue}
                    onChange={(e) => {
                      setDraftValue(e.target.value);
                      setDraftError("");
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter")
                        confirmCellEdit(row, activeEditableCol);
                      else if (e.key === "Escape") cancelCellEdit();
                    }}
                    onBlur={(e) => {
                      if (
                        !e.currentTarget.parentElement?.contains(
                          e.relatedTarget as Node,
                        )
                      ) {
                        confirmCellEdit(row, activeEditableCol, {
                          cancelOnInvalid: true,
                        });
                      }
                    }}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmCellEdit(row, activeEditableCol);
                    }}
                    className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0 rounded-r"
                    title="Save"
                  >
                    ✓
                  </button>
                </div>
              </div>
            </div>
          )}
        </td>
      );
    }
    return (
      <td key={col.key} className={firstColClass} style={mergedCellStyle}>
        -
      </td>
    );
  };

  const handlePreviousPage = () => {
    if (effectivePage > 1) handlePageSet(effectivePage - 1);
  };

  const handleNextPage = () => {
    if (effectivePage < totalPages) handlePageSet(effectivePage + 1);
  };

  const handleJump = () => {
    const page = parseInt(jumpValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      handlePageSet(page);
    }
    setJumpValue("");
  };

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    if (serverSidePagination && onServerItemsPerPageChange) {
      onServerItemsPerPageChange(newSize);
    }
    handlePageSet(1);
  };

  // Always returns exactly 7 slots so the layout never shifts.
  const getPageButtons = (): (number | "..." | null)[] => {
    const SLOTS = 7;
    if (totalPages <= SLOTS) {
      return [
        ...Array.from({ length: totalPages }, (_, i) => i + 1),
        ...Array<null>(SLOTS - totalPages).fill(null),
      ] as (number | null)[];
    }
    if (effectivePage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (effectivePage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      effectivePage - 1,
      effectivePage,
      effectivePage + 1,
      "...",
      totalPages,
    ];
  };

  useEffect(() => {
    dispatch(setLoadingFilterName(""));
  }, [dispatch]);

  useEffect(() => {
    if (!rowsDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!rowsDropdownRef.current?.contains(e.target as Node)) {
        setRowsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowsDropdownOpen]);

  // Compute which side to show the drop indicator based on drag direction
  const draggedIdx = draggedColKey ? columnOrder.indexOf(draggedColKey) : -1;
  const dragOverIdx = dragOverColKey ? columnOrder.indexOf(dragOverColKey) : -1;
  const dropSide =
    draggedIdx !== -1 && dragOverIdx !== -1 && draggedColKey !== dragOverColKey
      ? draggedIdx > dragOverIdx
        ? "left"
        : "right"
      : null;

  return (
    <div
      className={`chartTable ${isSmall ? "isSmall" : ""} flex flex-col tab-animatable-content relative ${disableCopy ? "no-copy" : ""}`}
      style={
        isSmall ? undefined : { height: tableHeight ?? "calc(100vh - 315px)" }
      }
      onCopy={blockCopyEvent}
      onCut={blockCopyEvent}
      onContextMenu={blockCopyEvent}
    >
      {showCriticalPeriods && !hideCriticalPeriodsButton && (
        <div style={{ zIndex: 400, position: "relative" }}>
          <CriticalPeriodsPanel
            periods={cpPeriods}
            activePeriodKinds={cpActiveKinds}
            onToggleAll={toggleCp}
          />
        </div>
      )}
      <style>{`
        @keyframes ctColMoved {
          0% { background-color: #fdd3bb; box-shadow: inset 0 -2px 0 0 #f1935c; }
          60% { background-color: #fff3ed; box-shadow: inset 0 -2px 0 0 rgba(241,147,92,0.4); }
          100% { background-color: transparent; box-shadow: none; }
        }
      `}</style>
      {/* In small mode, avoid inner vertical scrolling to prevent double scrollbars */}
      <div
        className={`${isSmall ? "overflow-x-auto overflow-y-visible" : "flex-1 min-h-0 overflow-auto text-nowrap"} capitalize relative w-full`}
        style={
          showCriticalPeriods && cpActive ? { paddingLeft: 22 } : undefined
        }
      >
        <table
          className={`${isSmall ? "w-full" : ""}`}
          // Always fixed layout so column widths are governed by the colgroup
          // below, never by cell content. This keeps widths stable when sorting
          // changes which rows (and thus which cell lengths) are visible.
          style={{ tableLayout: "fixed" }}
        >
          <colgroup>
            {orderedColumns.map((col) => {
              const userWidth = columnWidths[col.key];
              const measured = headerMinWidths[col.key] || 0;
              // Default width: measured header width clamped to [100, 280].
              // Once the user resizes a column, honour their width (no upper cap).
              const width = userWidth
                ? Math.max(userWidth, measured)
                : Math.min(280, Math.max(100, measured));
              return (
                <col
                  key={col.key}
                  style={{
                    width: `${width}px`,
                    minWidth: `${userWidth ? Math.max(100, measured) : Math.min(280, Math.max(100, measured))}px`,
                  }}
                />
              );
            })}
          </colgroup>
          <thead className="sticky top-0 z-[200]">
            <tr
              ref={theadRowRef}
              className={isSmall ? "text-[10px]" : "text-sm"}
            >
              {orderedColumns.map((col) => (
                <th
                  key={col.key}
                  className={`${isSmall ? "py-1 px-2" : "py-3 px-4"} cursor-pointer text-center relative select-none`}
                  style={{
                    opacity: draggedColKey === col.key ? 0.35 : 1,
                    transition:
                      "opacity 0.2s ease, background 0.15s ease, border-left 0.1s ease, border-right 0.1s ease",
                    background:
                      dragOverColKey === col.key && draggedColKey !== col.key
                        ? "#fff3ed"
                        : "white",
                    borderLeft:
                      dragOverColKey === col.key &&
                      dropSide === "left" &&
                      draggedColKey !== col.key
                        ? "2px solid #f1935c"
                        : undefined,
                    borderRight:
                      dragOverColKey === col.key &&
                      dropSide === "right" &&
                      draggedColKey !== col.key
                        ? "2px solid #f1935c"
                        : undefined,
                    animation:
                      recentlyMovedKey === col.key
                        ? "ctColMoved 0.55s ease"
                        : undefined,
                    ...(stickyColumnPositions[col.key]
                      ? {
                          position: "sticky" as const,
                          left: stickyColumnPositions[col.key].left,
                          top: 0,
                          zIndex: 300,
                          boxShadow: stickyColumnPositions[col.key].showShadow
                            ? "8px 0 14px -12px rgba(15, 23, 42, 0.42), 14px 0 24px -20px rgba(15, 23, 42, 0.24)"
                            : undefined,
                        }
                      : {}),
                    // Fixed layout: colgroup governs width; clip header overflow
                    // (ellipsis handled by the truncate span inside).
                    overflow: "hidden",
                  }}
                  title={col.label}
                  draggable
                  onDragStart={(e) => {
                    setDraggedColKey(col.key);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDragOverColKey(col.key);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!draggedColKey || draggedColKey === col.key) {
                      setDraggedColKey(null);
                      setDragOverColKey(null);
                      return;
                    }
                    const fromKey = draggedColKey;
                    setColumnOrder((order) => {
                      const next = [...order];
                      const fromIdx = next.indexOf(fromKey);
                      const toIdx = next.indexOf(col.key);
                      if (fromIdx === -1 || toIdx === -1) return order;
                      next.splice(fromIdx, 1);
                      next.splice(toIdx, 0, fromKey);
                      return next;
                    });
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                    setRecentlyMovedKey(fromKey);
                    window.setTimeout(() => setRecentlyMovedKey(null), 600);
                  }}
                  onDragEnd={() => {
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <div
                    ref={(el) => {
                      headerContentRefs.current[col.key] = el;
                    }}
                    className="flex items-center justify-center gap-1 whitespace-nowrap min-w-0"
                  >
                    <span
                      className="text-gray-400 cursor-grab active:cursor-grabbing shrink-0 hover:text-[#f1935c] transition-colors duration-150"
                      title="Drag to reorder"
                    >
                      ⠿
                    </span>
                    {(() => {
                      const match = col.label.match(
                        /(.*)\s*\((MW|MWH|MUS|Rs\/kWh|LU|₹\/kWh|Rs|%|°C|km\/h|mm|s|W\/m²)\)$/i,
                      );
                      if (match) {
                        const [, nameText, unitText] = match;
                        return (
                          <>
                            <span className="font-medium truncate">
                              {nameText}{" "}
                            </span>
                            <span className="text-[#34656D] ms-1 font-medium shrink-0">
                              ({unitText})
                            </span>
                          </>
                        );
                      } else {
                        return (
                          <span className="truncate">
                            {col.label.replace("()", "")}
                          </span>
                        );
                      }
                    })()}
                    <span className="ml-1 shrink-0">
                      {renderSortArrow(col.key)}
                    </span>
                  </div>
                  {/* Resize handle — drag right edge to resize column width */}
                  <div
                    className="absolute right-0 top-0 h-full w-5 cursor-col-resize z-[310] flex items-center justify-center group/rz"
                    title="Drag to resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const th = e.currentTarget
                        .parentElement as HTMLTableCellElement;
                      const key = col.key;
                      const startX = e.clientX;
                      const startWidth = th?.offsetWidth ?? 120;

                      // On first resize: snapshot ALL column offsetWidths from the DOM so
                      // switching to table-layout:fixed doesn't collapse un-resized columns
                      if (Object.keys(columnWidths).length === 0) {
                        const tr = th.closest("tr");
                        if (tr) {
                          const allThs = Array.from(
                            tr.querySelectorAll("th"),
                          ) as HTMLTableCellElement[];
                          const snapshot: Record<string, number> = {};
                          allThs.forEach((thEl, i) => {
                            const colKey = orderedColumns[i]?.key;
                            if (colKey) snapshot[colKey] = thEl.offsetWidth;
                          });
                          setColumnWidths(snapshot);
                        }
                      }

                      document.body.style.userSelect = "none";
                      document.body.style.cursor = "col-resize";
                      const floor = Math.max(100, headerMinWidths[key] || 100);
                      const onMove = (me: MouseEvent) => {
                        const newWidth = Math.max(
                          floor,
                          startWidth + me.clientX - startX,
                        );
                        setColumnWidths((prev) => ({
                          ...prev,
                          [key]: newWidth,
                        }));
                      };
                      const onUp = () => {
                        document.body.style.userSelect = "";
                        document.body.style.cursor = "";
                        document.removeEventListener("mousemove", onMove);
                        document.removeEventListener("mouseup", onUp);
                      };
                      document.addEventListener("mousemove", onMove);
                      document.addEventListener("mouseup", onUp);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="h-[45%] w-[2px] rounded-full bg-gray-300/60 group-hover/rz:bg-[#f1935c] group-hover/rz:h-[80%] group-hover/rz:w-[3px] group-hover/rz:shadow-sm transition-all duration-150" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            className={`text-[#8C8A96] font-normal ${isSmall ? "text-[11px]" : "text-[13px]"}`}
          >
            {(() => {
              // Pre-compute which row index is the middle of each cp run.
              const midRows = new Set<number>();
              let runStart = -1;
              let runKind: string | null = null;
              const flush = (endIdx: number) => {
                if (runStart === -1) return;
                midRows.add(Math.floor((runStart + endIdx) / 2));
                runStart = -1;
                runKind = null;
              };
              paginatedData.forEach((row, i) => {
                const k = getCriticalPeriodKind(row[block_key ?? "block"]);
                if (k !== runKind) {
                  flush(i - 1);
                  if (k) {
                    runStart = i;
                    runKind = k;
                  }
                }
              });
              flush(paginatedData.length - 1);

              return paginatedData.map((row, rowIndex) => {
                const cpKind = getCriticalPeriodKind(row[block_key ?? "block"]);
                const isMidRow = midRows.has(rowIndex);
                const cpColor =
                  cpKind === "solar"
                    ? "#f59e0b"
                    : cpKind === "evening"
                      ? "#f97316"
                      : cpKind === "night"
                        ? "#3b82f6"
                        : null;
                const cpMeta =
                  cpKind === "solar"
                    ? {
                        icon: "☀",
                        label: "Solar Hours",
                        time: "10:00–16:00",
                        iconKind: "solar",
                      }
                    : cpKind === "evening"
                      ? {
                          icon: "⚡",
                          label: "Evening Peak",
                          time: "17:00–21:00",
                          iconKind: "evening_ramp",
                        }
                      : cpKind === "night"
                        ? {
                            icon: "🌙",
                            label: "Night Low",
                            time: "00:00–05:00",
                            iconKind: "valley",
                          }
                        : null;
                const cpStyle: React.CSSProperties = cpKind
                  ? {
                      position: "relative",
                      borderLeft: `3px solid ${cpColor}`,
                      backgroundColor:
                        rowIndex === selectedRowIndex
                          ? undefined
                          : rowIndex === hoveredRowIndex
                            ? undefined
                            : cpKind === "solar"
                              ? "rgba(251,191,36,0.05)"
                              : cpKind === "evening"
                                ? "rgba(249,115,22,0.05)"
                                : "rgba(59,130,246,0.05)",
                    }
                  : {};
                return (
                  <tr
                    key={rowIndex}
                    onMouseEnter={() => setHoveredRowIndex(rowIndex)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    onClick={() =>
                      setSelectedRowIndex((prev) =>
                        prev === rowIndex ? null : rowIndex,
                      )
                    }
                    style={cpStyle}
                    className={`border-b text-center cursor-pointer ${
                      rowIndex === selectedRowIndex
                        ? "bg-orange-50 border-orange-200"
                        : rowIndex === hoveredRowIndex
                          ? "bg-gray-100 border-gray-200"
                          : "border-gray-200"
                    }`}
                  >
                    {orderedColumns.map((col, colIdx) =>
                      renderCell(col, row, {
                        isSticky: !!stickyColumnPositions[col.key],
                        stickyLeft: stickyColumnPositions[col.key]?.left,
                        showStickyShadow:
                          stickyColumnPositions[col.key]?.showShadow,
                        isSelected: rowIndex === selectedRowIndex,
                        isHovered: rowIndex === hoveredRowIndex,
                        rowIndex: rowIndex,
                        isMidRow: isMidRow,
                        cpKind: cpKind,
                        cpOverlay:
                          isMidRow && cpMeta && cpColor ? (
                            <span
                              style={{
                                position: "absolute",
                                left: -15,
                                top: 130,
                                transform:
                                  "translate(-50%, -50%) rotate(-90deg)",
                                transformOrigin: "center",
                                display: "inline-flex",
                                width: "max-content",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                zIndex: 999,
                              }}
                            >
                              <CriticalBandPill
                                icon={cpMeta.icon}
                                label={cpMeta.label}
                                time={cpMeta.time}
                                iconKind={cpMeta.iconKind}
                                color={cpColor}
                                noAnimation
                              />
                            </span>
                          ) : undefined,
                      }),
                    )}
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {paginatedData.length === 0 && showEmpty && (
        <div
          className="absolute inset-x-0 top-[52px] z-[100] pointer-events-none"
          style={{ bottom: isSmall ? 40 : 52 }}
        >
          <EmptyState compact={isSmall} />
        </div>
      )}

      {/* Loading overlay */}
      {resolvedLoading && (
        <div className="absolute inset-0 bg-white/60 z-[400] flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#f1935c] border-t-transparent rounded-full" />
        </div>
      )}

      {/* ── Pagination bar ── */}

      <div
        className={`flex flex-wrap items-center justify-between gap-2 shrink-0 bg-white border-t border-gray-200 z-[200] px-2 ${
          isSmall ? "text-[10px] py-1" : "text-sm py-2"
        }`}
      >
        {/* Left: record-range info + column controls */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-gray-500 whitespace-nowrap shrink-0 text-xs">
            {(() => {
              const total = serverSidePagination
                ? totalCount
                : sortedData.length;
              if (total === 0) return "No records";
              return `${(effectivePage - 1) * itemsPerPage + 1}–${Math.min(
                effectivePage * itemsPerPage,
                total,
              )} of ${total}`;
            })()}
          </span>

          {/* Column layout controls — always visible */}
          {(() => {
            return (
              <div className="flex items-center gap-1.5">
                {filename && (
                  <button
                    onClick={saveColumns}
                    disabled={!isDirty}
                    title={
                      !hasChanges
                        ? "Drag ⠿ to reorder columns · Drag column edge to resize"
                        : isDirty
                          ? "Save current layout so it loads next time"
                          : "Layout already saved"
                    }
                    className={`inline-flex items-center gap-1 rounded-md whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                      isSmall
                        ? "text-[9px] px-1.5 py-0.5"
                        : "text-[11px] px-2.5 py-1"
                    } ${
                      isDirty
                        ? "bg-[#f1935c] text-white shadow-sm hover:bg-[#e07840] active:scale-95 cursor-pointer"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                    }`}
                  >
                    {isDirty ? (
                      <>
                        <svg
                          className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                          <polyline points="17 21 17 13 7 13 7 21" />
                          <polyline points="7 3 7 8 15 8" />
                        </svg>
                        Save layout
                      </>
                    ) : (
                      <>
                        <svg
                          className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {hasChanges ? "Saved" : "Save layout"}
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={hasChanges ? resetColumns : undefined}
                  disabled={!hasChanges}
                  title={
                    hasChanges
                      ? "Reset column order and widths to default"
                      : "Drag ⠿ to reorder columns · Drag column edge to resize"
                  }
                  className={`inline-flex items-center gap-1 rounded-md border whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                    isSmall
                      ? "text-[9px] px-1.5 py-0.5"
                      : "text-[11px] px-2.5 py-1"
                  } ${
                    hasChanges
                      ? "border-gray-200 text-gray-500 bg-white hover:border-gray-300 hover:bg-gray-50 active:scale-95 cursor-pointer"
                      : "border-gray-200 text-gray-300 bg-white cursor-not-allowed opacity-60"
                  }`}
                >
                  <svg
                    className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                  Reset layout
                </button>

                {/* <button
                  onClick={handleDownloadCSV}
                  disabled={sortedData.length === 0}
                  title={`Download all ${sortedData.length} rows as CSV (current column order)`}
                  className={`inline-flex items-center gap-1 rounded-md border whitespace-nowrap shrink-0 transition-all duration-150 font-medium ${
                    isSmall
                      ? "text-[9px] px-1.5 py-0.5"
                      : "text-[11px] px-2.5 py-1"
                  } ${
                    sortedData.length > 0
                      ? "border-gray-200 text-gray-500 bg-white hover:border-gray-300 hover:bg-gray-50 active:scale-95 cursor-pointer"
                      : "border-gray-200 text-gray-300 bg-white cursor-not-allowed opacity-60"
                  }`}
                >
                  <svg
                    className={isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3v11" />
                    <path d="m7 10 5 5 5-5" />
                    <path d="M4 20h16" />
                  </svg>
                  Download CSV
                </button> */}

                {/* Hint tooltip when no changes made yet */}
                {!hasChanges && !isSmall && (
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-[10px] cursor-default select-none hover:bg-gray-50 hover:border-gray-400 hover:text-gray-500 transition-colors"
                    title="⠿ Drag a column header to reorder · Drag the right edge of a header to resize · Use 'Save layout' to persist your changes"
                  >
                    ?
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Centre: First / prev / page-numbers / next / last */}
        <div className="flex items-center gap-1">
          {/* First */}
          <button
            onClick={() => handlePageSet(1)}
            disabled={effectivePage === 1}
            title="First page"
            className={`inline-flex items-center justify-center border border-gray-200 rounded-md bg-white text-gray-500 font-medium transition-all duration-150 disabled:opacity-40 hover:bg-gray-50 hover:border-gray-300 active:scale-95 ${
              isSmall ? "min-w-5 h-5 text-[10px] px-1" : "min-w-7 h-7 text-xs px-1.5"
            }`}
          >
            «
          </button>

          {/* Previous */}
          <button
            onClick={handlePreviousPage}
            disabled={effectivePage === 1}
            title="Previous page"
            className={`inline-flex items-center justify-center border border-gray-200 rounded-md bg-white text-gray-500 font-medium transition-all duration-150 disabled:opacity-40 hover:bg-gray-50 hover:border-gray-300 active:scale-95 ${
              isSmall ? "min-w-5 h-5 text-[10px] px-1" : "min-w-7 h-7 text-xs px-1.5"
            }`}
          >
            ‹
          </button>

          {/* Page number buttons — always 7 fixed slots to prevent layout shift */}
          {getPageButtons().map((btn, idx) =>
            btn === null ? (
              <span
                key={`empty-${idx}`}
                aria-hidden
                className={`inline-block invisible ${
                  isSmall ? "min-w-5 h-5" : "min-w-7 h-7"
                }`}
              />
            ) : btn === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className={`inline-flex items-center justify-center select-none text-gray-400 ${
                  isSmall ? "min-w-5 h-5 text-[10px]" : "min-w-7 h-7 text-xs"
                }`}
              >
                …
              </span>
            ) : (
              <button
                key={btn}
                onClick={() => handlePageSet(btn as number)}
                className={`inline-flex items-center justify-center border rounded-md font-medium transition-all duration-150 active:scale-95 ${
                  isSmall ? "min-w-5 h-5 px-1 text-[10px]" : "min-w-7 h-7 px-1 text-xs"
                } ${
                  effectivePage === btn
                    ? "bg-[#f1935c] text-white border-[#f1935c] shadow-sm"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                {btn}
              </button>
            ),
          )}

          {/* Next */}
          <button
            onClick={handleNextPage}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Next page"
            className={`inline-flex items-center justify-center border border-gray-200 rounded-md bg-white text-gray-500 font-medium transition-all duration-150 disabled:opacity-40 hover:bg-gray-50 hover:border-gray-300 active:scale-95 ${
              isSmall ? "min-w-5 h-5 text-[10px] px-1" : "min-w-7 h-7 text-xs px-1.5"
            }`}
          >
            ›
          </button>

          {/* Last */}
          <button
            onClick={() => handlePageSet(totalPages)}
            disabled={effectivePage === totalPages || totalPages === 0}
            title="Last page"
            className={`inline-flex items-center justify-center border border-gray-200 rounded-md bg-white text-gray-500 font-medium transition-all duration-150 disabled:opacity-40 hover:bg-gray-50 hover:border-gray-300 active:scale-95 ${
              isSmall ? "min-w-5 h-5 text-[10px] px-1" : "min-w-7 h-7 text-xs px-1.5"
            }`}
          >
            »
          </button>
        </div>

        {/* Right: jump-to-page + rows-per-page */}
        <div className="flex items-center gap-3 ml-auto">
          {/* Jump to page */}
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 whitespace-nowrap text-xs">Go to</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJump()}
              placeholder="#"
              className={`border border-gray-200 rounded-md px-1.5 text-center outline-none focus:border-[#f1935c] transition-colors text-xs ${
                isSmall ? "w-10 h-5 text-[10px]" : "w-12 h-7"
              }`}
            />
            <button
              onClick={handleJump}
              className={`inline-flex items-center justify-center rounded-md bg-[#f1935c] text-white font-medium border border-[#f1935c] hover:bg-[#e07840] active:scale-95 transition-all duration-150 text-xs ${
                isSmall ? "px-1.5 h-5 text-[10px]" : "px-2.5 h-7"
              }`}
            >
              Go
            </button>
          </div>

          {/* Rows per page — custom dropdown */}
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500 whitespace-nowrap text-xs">Rows</span>
            <div ref={rowsDropdownRef} className="relative">
              <button
                onClick={() => setRowsDropdownOpen((o) => !o)}
                className={`inline-flex items-center gap-1.5 border border-gray-200 rounded-md bg-white text-gray-700 font-medium hover:border-gray-300 hover:bg-gray-50 active:scale-95 transition-all duration-150 text-xs ${
                  isSmall ? "h-5 px-1.5 text-[10px]" : "h-7 px-2.5"
                } ${rowsDropdownOpen ? "border-[#f1935c] ring-1 ring-[#f1935c]/20" : ""}`}
              >
                {itemsPerPage}
                <svg
                  className={`shrink-0 transition-transform duration-150 ${rowsDropdownOpen ? "rotate-180" : ""} ${isSmall ? "w-2.5 h-2.5" : "w-3 h-3"}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {rowsDropdownOpen && (
                <div className="absolute bottom-full mb-1.5 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-[500] min-w-[72px]">
                  {[25, 50, 96, 100, 200].map((n) => (
                    <button
                      key={n}
                      onClick={() => {
                        handleItemsPerPageChange(n);
                        setRowsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors duration-100 ${
                        itemsPerPage === n
                          ? "bg-[#f1935c] text-white"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
